import type { EventException, ScheduleEvent } from "@couples/shared";

import { supabase } from "../supabase";
import { createEvent, type EventInput } from "./events";

interface EventExceptionRow {
  id: string;
  couple_id: string;
  event_id: string;
  occurrence_date: string;
  override_event_id: string | null;
}

const COLUMNS = "id, couple_id, event_id, occurrence_date, override_event_id";

function toException(row: EventExceptionRow): EventException {
  return {
    id: row.id,
    coupleId: row.couple_id,
    eventId: row.event_id,
    occurrenceDate: row.occurrence_date,
    overrideEventId: row.override_event_id,
  };
}

/**
 * 繰り返し予定の「この回だけ」の例外を全件取得する。
 * events と同じく期間で絞らず全件取る(→ lib/schedule/events.ts の fetchEvents と同じ理由)。
 */
export async function fetchEventExceptions(): Promise<EventException[]> {
  const { data, error } = await supabase.from("event_exceptions").select(COLUMNS);
  if (error) throw error;
  const rows: EventExceptionRow[] = data;
  return rows.map(toException);
}

/** 繰り返し予定の、指定した回だけを消す(規則そのものは変えない) */
export async function skipEventOccurrence(eventId: string, occurrenceDate: string): Promise<void> {
  const { error } = await supabase
    .from("event_exceptions")
    .upsert(
      { event_id: eventId, occurrence_date: occurrenceDate, override_event_id: null },
      { onConflict: "event_id,occurrence_date" },
    );
  if (error) throw error;
}

/**
 * 繰り返し予定の、指定した回だけを差し替える。
 * 差し替え先は独立した単発の予定として作り(→ events テーブル)、例外テーブルで元の回と結び付ける。
 */
export async function createEventOccurrenceOverride(
  eventId: string,
  occurrenceDate: string,
  input: EventInput,
): Promise<ScheduleEvent> {
  const overrideEvent = await createEvent({
    ...input,
    recurrenceFreq: null,
    recurrenceInterval: 1,
    recurrenceUntil: null,
  });
  const { error } = await supabase
    .from("event_exceptions")
    .upsert(
      { event_id: eventId, occurrence_date: occurrenceDate, override_event_id: overrideEvent.id },
      { onConflict: "event_id,occurrence_date" },
    );
  if (error) throw error;
  return overrideEvent;
}
